/**
 * Notificação leve das mudanças de listas locais.
 *
 * O IndexedDB continua sendo a fonte da verdade; o evento carrega somente o id para que cada
 * consumidor releia o registro confirmado pela transação. BroadcastChannel mantém duas abas do
 * painel visualmente coerentes enquanto a Web Lock escolhe qual delas executa o upload.
 */

export const LOCAL_LIST_CHANGES_CHANNEL = 'eminer:local-list-changes:v1';

export interface LocalListChange {
  listId: string;
}

type LocalListChangeListener = (change: LocalListChange) => void;

const listeners = new Set<LocalListChangeListener>();
let channel: BroadcastChannel | undefined;

function isLocalListChange(value: unknown): value is LocalListChange {
  return (
    typeof value === 'object' &&
    value !== null &&
    'listId' in value &&
    typeof value.listId === 'string' &&
    value.listId.length > 0
  );
}

function emit(change: LocalListChange): void {
  for (const listener of listeners) listener(change);
}

function getChannel(): BroadcastChannel | undefined {
  if (channel || typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
    return channel;
  }

  channel = new window.BroadcastChannel(LOCAL_LIST_CHANGES_CHANNEL);
  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (isLocalListChange(event.data)) emit(event.data);
  });
  return channel;
}

/** Publica somente depois que a transação que alterou a lista foi confirmada. */
export function publishLocalListChange(listId: string): void {
  const change = { listId };
  emit(change);
  getChannel()?.postMessage(change);
}

export function subscribeLocalListChanges(listener: LocalListChangeListener): () => void {
  listeners.add(listener);
  getChannel();
  return () => listeners.delete(listener);
}

/** Isolamento dos testes; não faz parte do fluxo de produção. */
export function __resetLocalListChanges(): void {
  listeners.clear();
  channel?.close();
  channel = undefined;
}
